"use client";

import { c as _c } from "react/compiler-runtime";
import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiArrowLeftLine, RiArrowRightLine, RiCheckLine, RiExternalLinkLine } from "react-icons/ri";
import ReactMarkdown from "react-markdown";
import Image from "next/image";
import { useProgressNavigation } from "@/components/progress-bar";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { getCurrentModule } from "@/config/sitemap";
import { isSystemToastAction, parseSystemToastAction, runSystemToastAction } from "@/lib/system-toast-actions";
import { cx } from "@/lib/tailwind-utils";
import { SystemToast } from "@/types/system";
import { useTenant } from "./tenant-provider";
import { useUser } from "./user-provider";
import { fetchSystemToasts } from "@/api/system";
import { useSystemToasterStore } from "@/stores/system-toaster-store";
import { useTenantStore } from "@/stores/tenant-store";
let toasts: SystemToast[] = [];
let listeners: Array<(toasts: SystemToast[]) => void> = [];
function dispatch(toast: SystemToast) {
  const priorityMap = {
    error: 3,
    warning: 2,
    info: 1
  };
  toasts = [...toasts, {
    ...toast,
    createdAt: Date.now()
  }].sort((a, b) => (priorityMap[b.variant] || 0) - (priorityMap[a.variant] || 0));
  listeners.forEach(listener => listener(toasts));
}
function removeByIds(ids: Set<string>) {
  toasts = toasts.filter(t => !ids.has(t.id));
  listeners.forEach(listener => listener(toasts));
}
export function systemToast(toast: SystemToast) {
  dispatch({
    ...toast,
    displayDuration: toast.displayDuration
  });
}
function replaceContentPlaceholders(content: string, subDomain?: string): string {
  if (!content) return content;
  let processed = content;
  processed = processed.replace(/\\n/g, "\n");
  if (subDomain) {
    processed = processed.replace(/\{sub_domain\}/g, subDomain);
    processed = processed.replace(/%7Bsub_domain%7D/g, subDomain);
  }
  return processed;
}
const CarouselSlide = t0 => {
  const $ = _c(20);
  const {
    toast
  } = t0;
  const {
    tenant
  } = useTenant();
  const imageUrl = toast.imageUrls?.[0];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [failedUrls, setFailedUrls] = useState(t1);
  const t2 = tenant?.subDomain;
  let t3;
  if ($[1] !== t2 || $[2] !== toast.content) {
    t3 = replaceContentPlaceholders(toast.content, t2);
    $[1] = t2;
    $[2] = toast.content;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const processedContent = t3;
  let t4;
  if ($[4] !== failedUrls || $[5] !== imageUrl) {
    t4 = imageUrl && !failedUrls.has(imageUrl);
    $[4] = failedUrls;
    $[5] = imageUrl;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasImage = t4;
  let t5;
  if ($[7] !== hasImage || $[8] !== imageUrl) {
    t5 = hasImage && <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-background-tertiary"><Image src={imageUrl} alt="Feature preview" className="object-cover" fill={true} sizes="(max-width: 500px) 100vw, 500px" onError={() => setFailedUrls(prev => new Set(prev).add(imageUrl))} /></div>;
    $[7] = hasImage;
    $[8] = imageUrl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <div className="flex w-full flex-col items-center my-6">{t5}</div>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== tenant) {
    t7 = {
      h2: _temp,
      h3: _temp2,
      p: _temp3,
      ul: _temp4,
      li: _temp5,
      a: t8 => {
        const {
          href,
          children: children_4
        } = t8;
        if (!href) {
          return <span>{children_4}</span>;
        }
        const isExternal = href.startsWith("http") || href.startsWith("mailto:");
        let finalHref = href;
        if (!isExternal && tenant?.subDomain && !href.startsWith(`/${tenant.subDomain}`)) {
          finalHref = `/${tenant.subDomain}${href}`;
        }
        return <a href={finalHref} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined} className="text-primary underline">{children_4}</a>;
      }
    };
    $[12] = tenant;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = t7 as any;
  let t9;
  if ($[14] !== processedContent || $[15] !== t8) {
    t9 = <div className="w-full space-y-2 text-center"><ReactMarkdown components={t8}>{processedContent}</ReactMarkdown></div>;
    $[14] = processedContent;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <div>{t6}{t9}</div>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
export const SystemToaster = () => {
  const [localToasts, setLocalToasts] = useState<SystemToast[]>(toasts);
  const {
    updateViewedIds,
    getViewedIds
  } = useSystemToasterStore();
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const currentModule = getCurrentModule(window.location.pathname, tenant?.enabledModules);
  const [currentSlide, setCurrentSlide] = useState(0);
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const shownToastIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    listeners.push(setLocalToasts);
    return () => {
      listeners = listeners.filter(l => l !== setLocalToasts);
    };
  }, []);

  // Drop in-memory toasts when switching tenants so A/B lists don't leak.
  useEffect(() => {
    toasts = [];
    listeners.forEach(listener => listener(toasts));
    shownToastIdsRef.current = new Set();
    setCurrentSlide(0);
  }, [tenant?.subDomain]);

  // Fetch toasts directly from the public catalog.
  useEffect(() => {
    let cancelled = false;
    const requestedTenant = tenant?.subDomain;
    const fetchToasts = async () => {
      const catalogToasts = await fetchSystemToasts(requestedTenant);
      // Ignore stale responses after tenant switch / unmount.
      const activeTenant = useTenantStore.getState().getCurrentTenant();
      if (cancelled || requestedTenant !== activeTenant) return;
      if (catalogToasts) {
        const viewedIds = getViewedIds();
        catalogToasts.filter(toast => {
          if (toast.roleTypes && toast.roleTypes.length > 0) {
            const userRoleTypes = currentUser?.roles?.map(role => role.userType) || [];
            const hasMatchingRoleType = toast.roleTypes.some(toastRoleType => userRoleTypes.includes(toastRoleType));
            if (!hasMatchingRoleType) return false;
          }
          if (toast.module === "SYSTEM") {
            const systemIds = viewedIds["SYSTEM"] || [];
            const toastId = Number(toast.id);
            const highestSystemId = Math.max(0, ...systemIds);
            if (toastId <= highestSystemId) return false;
          }
          const moduleIds = viewedIds[toast.module] || [];
          const toastId_0 = Number(toast.id);
          const highestId = Math.max(0, ...moduleIds);
          if (toastId_0 <= highestId) return false;
          const isAlreadyShowing = toasts.some(t => t.id === toast.id);
          return !isAlreadyShowing;
        }).forEach(toast_0 => {
          systemToast(toast_0);
        });
      }
    };
    fetchToasts();
    return () => {
      cancelled = true;
    };
  }, [window.location.pathname, getViewedIds, currentUser, tenant?.subDomain]);
  const visibleToasts = useMemo(() => localToasts.filter(toast_1 => {
    const moduleMatches = toast_1.module === "SYSTEM" || toast_1.module === currentModule;
    if (!moduleMatches) return false;
    if (toast_1.roleTypes && toast_1.roleTypes.length > 0) {
      const userRoleTypes_0 = currentUser?.roles?.map(role_0 => role_0.userType) || [];
      return toast_1.roleTypes.some(toastRoleType_0 => userRoleTypes_0.includes(toastRoleType_0));
    }
    return true;
  }), [localToasts, currentModule, currentUser]);
  const totalSlides = visibleToasts.length;
  useEffect(() => {
    if (currentSlide >= totalSlides) {
      setCurrentSlide(Math.max(0, totalSlides - 1));
    }
  }, [totalSlides, currentSlide]);
  useEffect(() => {
    visibleToasts.forEach(toast_2 => {
      if (shownToastIdsRef.current.has(toast_2.id)) return;
      shownToastIdsRef.current.add(toast_2.id);
      posthog.capture("system_toast_shown", {
        toast_id: toast_2.id,
        module: toast_2.module,
        variant: toast_2.variant
      });
    });
  }, [visibleToasts]);
  const handleDismissAll = useCallback((source: "got_it" | "cta" | "outside" = "got_it") => {
    const dismissedIds = new Set<string>();
    visibleToasts.forEach(toast_3 => {
      const toastId_1 = Number(toast_3.id);
      if (Number.isFinite(toastId_1)) {
        updateViewedIds(toast_3.module, toastId_1);
      }
      dismissedIds.add(toast_3.id);
      posthog.capture("system_toast_dismissed", {
        toast_id: toast_3.id,
        module: toast_3.module,
        variant: toast_3.variant,
        source
      });
    });
    removeByIds(dismissedIds);
    setCurrentSlide(0);
  }, [visibleToasts, updateViewedIds]);
  const goToPrev = useCallback(() => {
    if (currentSlide <= 0) return;
    const next = currentSlide - 1;
    const target = visibleToasts[next];
    if (target) {
      posthog.capture("system_toast_slide_changed", {
        toast_id: target.id,
        module: target.module,
        variant: target.variant,
        direction: "prev"
      });
    }
    setCurrentSlide(next);
  }, [currentSlide, visibleToasts]);
  const isOpen = totalSlides > 0;
  const currentToast = isOpen ? visibleToasts[currentSlide] : null;
  const hasCta = currentToast?.ctaLabel && currentToast?.ctaLink;
  const isExternalCta = Boolean(currentToast?.ctaLink && (currentToast.ctaLink.startsWith("http") || currentToast.ctaLink.startsWith("mailto:")));
  const handleCtaClick = useCallback(() => {
    if (!currentToast?.ctaLink) return;
    const isAction = isSystemToastAction(currentToast.ctaLink);
    const isMailto = currentToast.ctaLink.startsWith("mailto:");
    const isExternal = !isAction && (currentToast.ctaLink.startsWith("http") || isMailto);
    posthog.capture("system_toast_cta_clicked", {
      toast_id: currentToast.id,
      module: currentToast.module,
      variant: currentToast.variant,
      cta_label: currentToast.ctaLabel,
      cta_link: isMailto ? "mailto:" : currentToast.ctaLink,
      is_external: isExternal,
      is_action: isAction
    });
    if (isAction) {
      const action = parseSystemToastAction(currentToast.ctaLink);
      if (action) runSystemToastAction(action);
    } else if (isExternal) {
      window.open(currentToast.ctaLink, "_blank", "noopener,noreferrer");
    } else {
      navigateWithProgress(currentToast.ctaLink);
    }
    handleDismissAll("cta");
  }, [currentToast, handleDismissAll]);
  return <Dialog open={isOpen} onOpenChange={open => !open && handleDismissAll("outside")}>
      <DialogContent className="max-w-[460px] p-0">
        <DialogTitle className="sr-only">System announcements</DialogTitle>
        <DialogDescription className="sr-only">
          Updates and announcements from the team
        </DialogDescription>

        {/* Slide content */}
        {isOpen && <DialogBody>
            <CarouselSlide toast={visibleToasts[currentSlide]} />
          </DialogBody>}

        {/* Dots + Navigation */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <Button onClick={goToPrev} disabled={currentSlide === 0} variant="ghost" size="icon-sm" aria-label="Previous" icon={<RiArrowLeftLine className="size-4" />} className={cx("transition-opacity duration-200", currentSlide === 0 && "opacity-0")} />
          {/* Dot indicators */}
          {totalSlides > 1 && <div className="flex items-center gap-1.5">
              {visibleToasts.map((_, index) => <button key={index} onClick={() => {
            const target_0 = visibleToasts[index];
            if (target_0 && index !== currentSlide) {
              posthog.capture("system_toast_slide_changed", {
                toast_id: target_0.id,
                module: target_0.module,
                variant: target_0.variant,
                direction: "dot"
              });
            }
            setCurrentSlide(index);
          }} className={cx("h-2 rounded-full transition-all duration-200", index === currentSlide ? "w-5 bg-foreground" : "w-2 bg-foreground-muted/40 hover:bg-foreground-muted")} aria-label={`Go to slide ${index + 1}`} />)}
            </div>}

          {/* CTA or Next/Dismiss */}
          {hasCta ? <Button variant="primary" onClick={handleCtaClick} className="gap-1.5 px-5 self-end">
              {currentToast.ctaLabel}
              {isExternalCta ? <RiExternalLinkLine className="size-4 shrink-0" /> : <RiArrowRightLine className="size-4 shrink-0" />}
            </Button> : <Button variant="primary" onClick={() => handleDismissAll("got_it")} className="px-5 self-end" icon={<RiCheckLine className="size-4" />}>
              Got it
            </Button>}
        </div>
      </DialogContent>
    </Dialog>;
};
function _temp(t0) {
  const {
    children
  } = t0;
  return <h2 className="text-h3 text-foreground mb-6">{children}</h2>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <h3 className="text-h4 text-foreground mb-6">{children_0}</h3>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <p className="text-body leading-relaxed text-foreground-secondary text-left">{children_1}</p>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <ul className="my-2 list-disc pl-5 text-left text-body text-foreground-secondary">{children_2}</ul>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <li>{children_3}</li>;
}